import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
// Centralized lodash imports - much cleaner!
import {
  map,
  slice,
  filter,
  ceil,
  intersection,
  intersectionBy,
  toLower,
  includes,
  pull,
  get,
  split,
  every,
  isNil,
  orderBy,
  compact,
  concat,
  remove,
  isEmpty,
  meanBy,
} from '@/utils/lodash'

import {
  GET_LENDER_PROPERTIES,
  GET_SHARED_PROPERTIES,
  GET_PROPERTIES,
  GET_JV_PROPERTIES,
} from '@/housefolios-components/Properties/queries'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
import {
  getSorting,
  stableSort,
} from '@/housefolios-components/Properties/sortTable'
import { useQuery } from "@apollo/client/react";
import { GET_TRASH } from '../housefolios-components/Properties/queries'
import TrashProperties from '@/housefolios-components/Properties/TrashProperties'
import {
  GET_COMMERCIAL_PROPERTIES,
  GET_LOTS,
} from '@/housefolios-components/Lots/queries'
import { GET_MARKETPLACES } from '@/housefolios-components/Marketplace/queries'

export const KindOfPage = Object.freeze({
  MY_PROPERTIES: 'my-properties',
  TRASH: 'trash',
  SEARCH: 'search',
  MARKETPLACE: 'marketplace',
  PUBLIC_MARKETPLACE: 'public-marketplace',
  LOTS: 'lots',
  COMMERCIAL: 'commercial',
})

function useProperties(props) {
  const {
    isTrashedProperties = false,
    kind = KindOfPage.MY_PROPERTIES,
    marketplace,
    propertySearch = false,
    searchResults,
  } = props
  const selectedPortfolio = localStorage.getItem('portfolio')
    ? localStorage.getItem('portfolio')
    : ''
  const [selected, setSelected] = useState([])

  const [marketplaceSelected, setMarketplaceSelected] = useState([])
  const [search, setSearch] = useState('')
  const [filtered, setFiltered] = useState(false)
  const [filteredProperties, setFilteredProperties] = useState([])
  const [searchedProperties, setSearchedProperties] = useState([])
  const [ordering, setOrdering] = useState('')
  const [
    propertySearchOrderBy,
    marketOrderBy,
    defaultOrderBy,
    order,
  ] = split(ordering, '-')
  const [page, setPage] = useState(
    parseInt(
      localStorage.getItem(
        kind === KindOfPage.TRASH
          ? 'trash-page'
          : kind === KindOfPage.SEARCH
            ? 'propertySearch-page'
            : marketplace
              ? marketplace === 'null'
                ? `housefolios-marketplace-page`
                : `${marketplace}-marketplace-page`
              : selectedPortfolio === 'null' || !selectedPortfolio
                ? kind === KindOfPage.LOTS
                  ? 'myLots-page'
                  : kind === KindOfPage.COMMERCIAL
                    ? 'commercial-page'
                    : 'myProperties-page'
                : `${selectedPortfolio}-portfolio-page`,
      ),
    ) || 1,
  )

  const [allProperties, setAllProperties] = useState([])
  const fetchingAllPages = useRef(false)

  // Fetch Properties — memoize variables so AC4 does not treat each render as a new query.
  const queryVariables = useMemo(() => {
    if (props.jv) {
      return {
        jvLenders: [
          props.session.me.activeOrg.submittedProperties.jv,
        ],
      }
    }
    if (props.lender) {
      return {
        lenders: [
          props.session.me.activeOrg.submittedProperties.lender,
        ],
      }
    }
    if (props.shared) {
      return {}
    }

    const portfolioIds =
      selectedPortfolio !== 'null' && selectedPortfolio
        ? [props?.portfolioData?._id]
        : map(props?.portfolios, (portfolio) => portfolio._id)

    return {
      portfolioId: portfolioIds,
      limit: GET_PROPERTIES_QUERY_LIMIT,
    }
  }, [
    props.jv,
    props.lender,
    props.shared,
    props.session?.me?.activeOrg?.submittedProperties?.jv,
    props.session?.me?.activeOrg?.submittedProperties?.lender,
    props?.portfolioData?._id,
    props?.portfolios,
    selectedPortfolio,
  ])

  const {
    loading: propertiesQueryLoading,
    error: propertiesError,
    data: propertiesData,
    fetchMore: fetchMoreProperties,
  } = useQuery(
    props.jv
      ? GET_JV_PROPERTIES
      : props.lender
        ? GET_LENDER_PROPERTIES
        : props.shared
          ? GET_SHARED_PROPERTIES
          : GET_PROPERTIES,
    {
      variables: queryVariables,
      skip:
        !props?.portfoliosData && kind !== KindOfPage.MY_PROPERTIES,
    },
  )
  // Keep showing results while a background refetch/fetchMore is in flight.
  const loadingProperties =
    propertiesQueryLoading && !propertiesData?.properties && !propertiesData?.jvProperties && !propertiesData?.lenderProperties && !propertiesData?.sharedProperties

  // Fetch Trash Properties
  const {
    loading: loadingTrashedProperties,
    data: trashedPropertiesData,
    error: trashedPropertiesError,
  } = useQuery(GET_TRASH, {
    skip: kind !== KindOfPage.TRASH,
  })

  // Fetch lots
  const {
    loading: loadingLots,
    data: lotsData,
    error: lotsError,
  } = useQuery(GET_LOTS, {
    skip: kind !== KindOfPage.LOTS,
    variables:
      selectedPortfolio !== 'null'
        ? { portfolioId: props?.portfolioData?._id }
        : {
            portfolioId: map(
              props?.lotPortfolios,
              (portfolio) => portfolio?._id,
            ),
          },
  })

  // Fetch lots
  const {
    loading: loadingCommercial,
    data: commercialData,
    error: commercialError,
  } = useQuery(GET_COMMERCIAL_PROPERTIES, {
    skip: kind !== KindOfPage.COMMERCIAL,
    variables:
      selectedPortfolio !== 'null'
        ? { portfolioId: [props?.portfolioData?._id] }
        : {
            portfolioId: map(
              props?.commercialPortfolios,
              (portfolio) => portfolio?._id,
            ),
          },
  })

  // Fetch marketplace properties
  const {
    loading: loadingMarketPlaceData,
    error: marketPlaceError,
    data: marketPlaceData,
    fetchMore: fetchMoreMarketPlaceProperties,
  } = useQuery(
    GET_MARKETPLACES,
    kind === KindOfPage.MARKETPLACE
      ? {
          skip: kind !== KindOfPage.MARKETPLACE,
          variables: {
            marketplaces:
              marketplace !== 'null' && marketplace
                ? [marketplace]
                : props?.session?.me?.activeOrg?.member === 'RWN'
                  ? map(
                      props.session.me.activeOrg.marketPlaces,
                      (market) => market.url,
                    )
                  : ['housefolios', 'myGrayLabel'],
            approved: !props.submitted,
            limit: GET_PROPERTIES_QUERY_LIMIT,
          },
        }
      : kind === KindOfPage.PUBLIC_MARKETPLACE
        ? {
            skip: kind !== KindOfPage.PUBLIC_MARKETPLACE,
            variables: {
              marketplaces:
                marketplace !== 'null' && marketplace
                  ? [marketplace]
                  : ['housefolios', 'myGrayLabel'],
              approved: true,
              sold: props.sold ? true : false,
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          }
        : {
            skip: true,
          },
  )

  // Function to get search results based on input string
  const getSearchResults = (string) => {
    // console.log('getSearchResults called with:', string)
    // console.log('search', search)
    // Check if the search string is the same as the current search
    if (search === string) return null
    // Split the search string into words and create regex filters
    const words = split(string, ' ')
    const searchFilters = map(
      words,
      (word) => new RegExp(toLower(word)),
    )
    // Filter properties based on search criteria
    // console.log('searchResults', searchResults)
    const filtered = filter(
      kind === KindOfPage.LOTS
        ? lotsData.lots
        : kind === KindOfPage.COMMERCIAL
          ? commercialData.commercialProperties
          : kind === KindOfPage.TRASH
            ? trashedPropertiesData?.trashedProperties
            : kind === KindOfPage.SEARCH
              ? searchResults
              : kind === KindOfPage.MARKETPLACE ||
                  kind === KindOfPage.PUBLIC_MARKETPLACE
                ? marketPlaceData?.marketplaceProperties?.properties
                : props.jv
                  ? propertiesData?.jvProperties
                  : props.lender
                    ? propertiesData?.lenderProperties
                    : props.shared
                      ? propertiesData?.sharedProperties
                      : propertiesData.properties?.properties,

      (property) => {
        const { createdBy } = property
        const profile = createdBy
          ? createdBy.profile
          : { firstname: '', lastname: '' }
        return every(
          searchFilters,
          (searchFilter) =>
            searchFilter.test(toLower(property.address)) ||
            searchFilter.test(toLower(property.city)) ||
            searchFilter.test(toLower(property.state)) ||
            searchFilter.test(toLower(property.zip)) ||
            searchFilter.test(toLower(property.sqft)) ||
            searchFilter.test(toLower(property.description)) ||
            searchFilter.test(toLower(profile.firstname)) ||
            searchFilter.test(toLower(profile.lastname)),
        )
      },
    )
    // Update search state and set searched properties
    setSearch(string)
    setSearchedProperties(filtered)
  }

  // Function to filter properties based on a specific criteria
  const getFilteredProperties = (filtered) => {
    const nextIds = (filtered || []).map((p) => p?._id).join(',')
    setFilteredProperties((prev) => {
      const prevIds = (prev || []).map((p) => p?._id).join(',')
      return prevIds === nextIds ? prev : filtered
    })
    setFiltered((prev) => (prev === true ? prev : true))
  }

  // Logic to order and filter properties based on search and filter criteria
  let properties =
    kind === KindOfPage.LOTS
      ? orderBy(
          search && filtered
            ? intersectionBy(
                searchedProperties,
                filteredProperties,
                '_id',
              )
            : search
              ? searchedProperties
              : filtered && filteredProperties
                ? filteredProperties
                : lotsData?.lots || [],
          'createdAt',
          'desc',
        )
      : kind === KindOfPage.COMMERCIAL
        ? orderBy(
            search && filtered
              ? intersectionBy(
                  searchedProperties,
                  filteredProperties,
                  '_id',
                )
              : search
                ? searchedProperties
                : filtered && filteredProperties
                  ? filteredProperties
                  : commercialData?.commercialProperties || [],
            'createdAt',
            'desc',
          )
        : kind === KindOfPage.TRASH
          ? orderBy(
              search && filtered
                ? intersectionBy(
                    searchedProperties,
                    filteredProperties,
                    '_id',
                  )
                : search
                  ? searchedProperties
                  : filtered && filteredProperties
                    ? filteredProperties
                    : trashedPropertiesData?.trashedProperties || [],
              'deletedAt',
              'desc',
            )
          : [
                KindOfPage.MARKETPLACE,
                KindOfPage.PUBLIC_MARKETPLACE,
              ].includes(kind)
            ? orderBy(
                search && filtered
                  ? intersectionBy(
                      searchedProperties,
                      filteredProperties,
                      '_id',
                    )
                  : search
                    ? searchedProperties
                    : filtered && filteredProperties
                      ? filteredProperties
                      : marketPlaceData?.marketplaceProperties
                          ?.properties || [],
                'marketplaces.expirationDate',
                'desc',
              )
            : orderBy(
                search && filtered
                  ? intersectionBy(
                      searchedProperties,
                      filteredProperties,
                      '_id',
                    )
                  : search
                    ? searchedProperties
                    : filtered && filteredProperties
                      ? filteredProperties
                      : props.jv
                        ? propertiesData?.jvProperties
                        : props.lender
                          ? propertiesData?.lenderProperties || []
                          : props.shared
                            ? propertiesData?.sharedProperties || []
                            : propertySearch
                              ? searchResults || []
                              : propertiesData?.properties
                                  ?.properties || [],
                'createdAt',
                'desc',
              )

  if (kind === KindOfPage.PUBLIC_MARKETPLACE) {
    if (!marketplace) {
      properties = filter(
        properties,
        (property) =>
          new Date(property.marketplaces.expirationDate).getTime() >
          Date.now(),
      )
    }
    properties = props.sold
      ? filter(
          properties,
          (property) => toLower(property?.status || '') === 'sold',
        )
      : filter(
          properties,
          (property) => toLower(property?.status || '') !== 'sold',
        )
  }

  // Calculate the number of pages based on number of properties
  const count = ceil(properties.length / 24)

  useEffect(() => {
    if (page > count) setPage(count || 1)
  }, [])

  useEffect(() => {
    setSelected([])
    setMarketplaceSelected([])
    setSearch('')
    switch (kind) {
      case KindOfPage.MY_PROPERTIES:
        // console.log('useEffect run')
        setPage(
          parseInt(
            localStorage.getItem(
              selectedPortfolio === 'null' || !selectedPortfolio
                ? 'myProperties-page'
                : `${selectedPortfolio}-portfolio-page`,
            ),
          ) || 1,
        )
        break
      case KindOfPage.MARKETPLACE:
      case KindOfPage.PUBLIC_MARKETPLACE:
        setPage(
          parseInt(
            localStorage.getItem(
              marketplace === 'null' || !marketplace
                ? 'housefolios-marketplace-card-page'
                : `${marketplace}-marketplace-card-page`,
            ),
          ) || 1,
        )
        break
      default:
        break
    }
  }, [selectedPortfolio, marketplace])

  // Function to select a property by its ID
  const selectProperty = (id) => (event) => {
    event.preventDefault()
    let newSelect = [...selected]
    if (includes(newSelect, id)) pull(newSelect, id)
    else newSelect.push(id)
    setSelected(newSelect)
  }

  // Get a list of properties based on current page
  const currentPageProperties = useMemo(() => {
    return slice(
      stableSort(
        compact(properties),
        getSorting(
          order,
          kind === KindOfPage.SEARCH
            ? propertySearchOrderBy
            : defaultOrderBy,
          marketOrderBy,
        ),
      ),
      (page - 1) * 24,
      page * 24,
    )
  }, [
    page,
    properties,
    defaultOrderBy,
    marketOrderBy,
    propertySearchOrderBy,
  ])

  // Function to select all properties for more operations
  const selectAll = () => {
    switch (kind) {
      case KindOfPage.MY_PROPERTIES:
        selectAllSharedLogic(
          properties,
          setSelected,
          setMarketplaceSelected,
          props,
          propertySearch,
        )
        break
      case KindOfPage.MARKETPLACE:
      case KindOfPage.PUBLIC_MARKETPLACE:
        selectAllSharedLogic(
          properties,
          setSelected,
          setMarketplaceSelected,
          props,
          propertySearch,
        )
        break
      case KindOfPage.TRASH:
        setSelected(map(properties, (property) => property._id))
        break
      case KindOfPage.SEARCH:
        let unownedProperties = filter(
          properties,
          (property) =>
            get(property, 'portfolio.organization._id') !==
            props.session.me.activeOrg._id,
        )
        const searchCompare = intersection(
          map(unownedProperties, (property) => property._id),
          concat(selected, marketplaceSelected),
        )
        if (searchCompare.length < unownedProperties.length) {
          let search = [...unownedProperties]
          const market = remove(
            search,
            (property) =>
              !isEmpty(
                get(property, 'marketplaces.approvedMarketplace'),
              ),
          )
          setSelected(map(search, (property) => property._id))
          setMarketplaceSelected(
            map(market, (property) => property._id),
          )
        } else {
          setSelected([])
          setMarketplaceSelected([])
        }
        break
      case KindOfPage.LOTS:
        setSelected(map(properties, (property) => property._id))
        break
      case KindOfPage.COMMERCIAL:
        setSelected(map(properties, (property) => property._id))
        break
      default:
        break
    }
  }

  function selectAllSharedLogic(
    properties,
    setSelected,
    setMarketplaceSelected,
    props,
    propertySearch,
  ) {
    // Collect all relevant properties.
    let ownedProperties = [...properties]
    // Filter out properties based on ownership criteria.
    let unownedProperties = remove(
      ownedProperties,
      (property) =>
        !(
          isNil(get(property, 'portfolio.organization._id')) &&
          !propertySearch
        ) &&
        get(property.node, 'portfolio.organization._id') !==
          props.session.me.activeOrg._id,
    )
    // Update the selected properties IDs.
    setSelected(map(ownedProperties, (property) => property._id))
    // Update the marketplace selected properties IDs.
    setMarketplaceSelected(
      map(unownedProperties, (property) => property._id),
    )
  }

  const selectMarketplaceProperty = (id) => (event) => {
    event.preventDefault()
    let newSelect = [...marketplaceSelected]
    if (includes(newSelect, id)) pull(newSelect, id)
    else newSelect.push(id)
    setMarketplaceSelected(newSelect)
  }

  const propertyValues = map(properties, (property) => {
    return {
      _id: property._id,
      propertyPrice: Number(
        property.price ||
          get(property, 'acquisition.analysis.resellPrice'),
      ),
      propertyPerSqft: property.sqft
        ? Number(
            property.price ||
              get(property, 'acquisition.analysis.resellPrice'),
          ) / property.sqft || 0
        : null,
      sqft: property.sqft,
    }
  })

  const averagePerSqft = meanBy(
    filter(propertyValues, 'propertyPerSqft'),
    'propertyPerSqft',
  )

  const handlePageChange = (event, value) => {
    switch (kind) {
      case KindOfPage.MY_PROPERTIES:
        if (selectedPortfolio === 'null' || !selectedPortfolio)
          localStorage.setItem('myProperties-page', value)
        else
          localStorage.setItem(
            `${selectedPortfolio}-portfolio-page`,
            value,
          )
        setPage(value || 1)
        break
      case KindOfPage.TRASH:
        localStorage.setItem(`trash-page`, value)
        setPage(value)
        break
      case KindOfPage.SEARCH:
        localStorage.setItem('propertySearch-page', value)
        setPage(value)
        break
      case KindOfPage.MARKETPLACE:
      case KindOfPage.PUBLIC_MARKETPLACE:
        localStorage.setItem(
          marketplace
            ? marketplace === 'null'
              ? 'housefolios-marketplace-page'
              : `${marketplace}-marketplace-page`
            : 'housefolios-marketplace-page',
          value,
        )
        setPage(value)
        break
      case KindOfPage.LOTS:
        localStorage.setItem('myLots-page', value)
        setPage(value)
        break
      case KindOfPage.COMMERCIAL:
        localStorage.setItem('commercial-page', value)
        setPage(value)
        break
      default:
        break
    }
  }

  const handleRequestSort = (
    event,
    property,
    market,
    propertySearch,
  ) => {
    const isDesc = defaultOrderBy === property && order === 'asc'
    const newOrder = isDesc ? 'desc' : 'asc'
    const orderBy = property
    const marketOrderBy = market ? market : property
    const propertySearchOrderBy = propertySearch
      ? propertySearch
      : property
    const newOrdering =
      propertySearchOrderBy +
      '-' +
      marketOrderBy +
      '-' +
      orderBy +
      '-' +
      newOrder
    setOrdering(newOrdering)
  }

  // If fetchMoreProperties is used, wrap it to log calls
  const fetchMorePropertiesWithLog = useCallback(
    (...args) => {
      return fetchMoreProperties(args[0]).then((result) => {
        return result
      })
    },
    [fetchMoreProperties],
  )

  // Helper to auto-fetch all pages for ALL view
  useEffect(() => {
    // Only for residential, ALL view, and when propertiesData is available
    if (
      kind === KindOfPage.MY_PROPERTIES &&
      props.allViewIsSelected &&
      propertiesData &&
      propertiesData.properties &&
      propertiesData.properties.hasNextPage &&
      !fetchingAllPages.current
    ) {
      fetchingAllPages.current = true
      let all = [...(propertiesData.properties.properties || [])]
      let cursor = propertiesData.properties.cursor

      const fetchAll = async () => {
        let hasNext = propertiesData.properties.hasNextPage
        while (hasNext) {
          const result = await fetchMoreProperties({
            variables: { cursor },
          })
          const newProps = result.data.properties.properties
          all = all.concat(newProps)
          hasNext = result.data.properties.hasNextPage
          cursor = result.data.properties.cursor
        }
        setAllProperties(all)
        fetchingAllPages.current = false
      }
      fetchAll()
    } else if (
      kind === KindOfPage.MY_PROPERTIES &&
      props.allViewIsSelected &&
      propertiesData &&
      propertiesData.properties &&
      !propertiesData.properties.hasNextPage
    ) {
      setAllProperties(propertiesData.properties.properties || [])
    }
  }, [kind, props.allViewIsSelected, propertiesData])

  // Selection helpers for robust ALL/PAGE logic
  const selectPage = () => {
    const pageIds = map(
      currentPageProperties,
      (property) => property._id,
    )
    setSelected((prev) => Array.from(new Set([...prev, ...pageIds])))
  }

  const deselectPage = () => {
    const pageIds = map(
      currentPageProperties,
      (property) => property._id,
    )
    setSelected((prev) => prev.filter((id) => !pageIds.includes(id)))
  }

  const deselectAll = () => {
    setSelected([])
    // setTimeout(() => setSelected([]), 0); // Force clear in next tick
  }

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id],
    )
  }

  return [
    {
      selected,
      filtered,
      filteredProperties,
      searchedProperties,
      loadingProperties,
      propertiesError,
      propertiesData,
      properties,
      loadingMarketPlaceData,
      marketPlaceError,
      marketPlaceData,
      count,
      currentPageProperties,
      ordering,
      averagePerSqft,
      trashedProperties: trashedPropertiesData?.trashedProperties,
      trashedPropertiesError,
      loadingTrashedProperties,
      selectedPortfolio,
      marketplaceSelected,
      search,
      page,
      loadingLots,
      lotsData,
      lotsError,
      loadingCommercial,
      commercialData,
      commercialError,
    },
    {
      setSelected,
      setPage,
      setSearch,
      setMarketplaceSelected,
      fetchMoreProperties:
        kind === KindOfPage.MARKETPLACE ||
        kind === KindOfPage.PUBLIC_MARKETPLACE
          ? fetchMoreMarketPlaceProperties
          : fetchMorePropertiesWithLog,
      setFiltered,
      setFilteredProperties,
      setSearchedProperties,
      selectAll,
      selectPage,
      deselectAll,
      deselectPage,
      toggleSelect,
      selectProperty,
      getSearchResults,
      getFilteredProperties,
      setOrdering,
      selectMarketplaceProperty,
      handlePageChange,
      handleRequestSort,
    },
  ]
}

export default useProperties
